<template>
    <div class="card">
        <PageHeader
            :showBtn="false"
            title="Task Management"
            :showAddNew="true"
            :showExtraBtn="true"
            extraBtnTitle="Calendar View"
            btnTitle="Add New"
            @onClickExtraBtn="navigateToCalendarPage"
            @onClickAddNewButton="onClickAddNew"
        />

        <div class="task-tabs">
            <button
                v-for="tab in visibleTaskTabs"
                :key="tab.key"
                type="button"
                class="task-tab"
                :class="{ 'is-active': activeTab === tab.key }"
                @click="onClickTab(tab.key)"
            >{{ tab.label }}</button>
        </div>

        <div class="filter-section">
            <div class="filter-bar">
                <div v-if="! isTodayTab" class="filter-dates">
                    <div class="filter-date-field">
                        <label class="filter-label">From</label>
                        <input
                            v-model="start"
                            @change="updateDateQuery"
                            type="date"
                            class="form-control filter-date-input"
                        >
                    </div>
                    <div class="filter-date-field">
                        <label class="filter-label">To</label>
                        <input
                            v-model="end"
                            @change="updateDateQuery"
                            type="date"
                            class="form-control filter-date-input"
                        >
                    </div>
                    <button class="btn btn-primary filter-go-btn" @click="onClickToGo">Go</button>
                </div>

                <div class="filter-bar-actions">
                    <FilterPopover
                        ref="filterPopoverRef"
                        :active-count="activeFilterCount"
                        @open="onOpenFilter"
                        @apply="onApplyFilters"
                        @clear="onClearFilters"
                    >
                        <div v-if="isUserAccess" class="col-12">
                            <label class="filter-label">Team Member</label>
                            <AsyncSelect
                                placeholder="Any team member"
                                v-model="draftFilters.contactProfile"
                                :api-service="fetchContactProfiles"
                                :additional-query="{roles: 'employee', status: 'active'}"
                                label="name"
                            />
                        </div>

                        <div class="col-12 col-sm-6">
                            <label class="filter-label">Activity Group</label>
                            <AsyncSelect
                                placeholder="Any group"
                                v-model="draftFilters.activityGroup"
                                :additional-query="{type: 'group'}"
                                :api-service="fetchActivitySetupEndpoint"
                                label="name"
                                :option-selected="onChangeActivityGroup"
                            />
                        </div>

                        <div class="col-12 col-sm-6">
                            <label class="filter-label">Organization Activity</label>
                            <AsyncSelect
                                placeholder="Any activity"
                                v-model="draftFilters.organizationActivity"
                                :api-service="fetchActivitySetupEndpoint"
                                :additional-query="{ group_id: draftFilters.activityGroup && draftFilters.activityGroup.id }"
                                label="name"
                                ref="organizationActivityDropdown"
                            />
                        </div>

                        <div class="col-12 col-sm-6">
                            <label class="filter-label">Client</label>
                            <AsyncSelect
                                placeholder="Any client"
                                v-model="draftFilters.client"
                                :api-service="fetchContactProfiles"
                                :additional-query="{roles: 'customer,lead'}"
                                label="name"
                            />
                        </div>

                        <div class="col-12 col-sm-6">
                            <label class="filter-label">Project</label>
                            <v-select
                                placeholder="Any project"
                                v-model="draftFilters.project"
                                :options="projects"
                                label="name"
                            />
                        </div>

                        <div v-if="isAllTaskTab || isTodayTab" class="col-12 col-sm-6">
                            <label class="filter-label">Task Role</label>
                            <v-select
                                placeholder="Any role"
                                :options="taskCalendarFilterList"
                                v-model="draftFilters.taskCalendarType"
                                label="name"
                                :reduce="name => name.value"
                            />
                        </div>

                        <div v-if="isAllTaskTab" class="col-12 col-sm-6">
                            <label class="filter-label">Task Status</label>
                            <v-select
                                placeholder="Any status"
                                :options="taskCalendarStatusList"
                                v-model="draftFilters.taskCalendarStatus"
                                label="name"
                                :reduce="name => name.value"
                            />
                        </div>

                        <div v-if="isMeetingMinuteTab" class="col-12 col-sm-6">
                            <label class="filter-label">Sub-task Status</label>
                            <v-select
                                placeholder="Any status"
                                :options="subtaskStatusOptions"
                                v-model="draftFilters.subtaskStatus"
                                label="name"
                                :reduce="option => option.value"
                            />
                        </div>
                    </FilterPopover>

                    <button
                        type="button"
                        class="btn btn-outline-secondary btn-sm"
                        :disabled="loading"
                        @click="exportExcel"
                    >
                        <i class="fa-regular fa-file-excel"></i>
                        Export
                    </button>
                </div>
            </div>

            <div v-if="activeFilterChips.length" class="filter-chips">
                <span v-for="chip in activeFilterChips" :key="chip.key" class="filter-chip">
                    <span class="filter-chip-label">{{ chip.label }}:</span>
                    {{ chip.text }}
                    <button type="button" class="filter-chip-remove" @click="removeFilter(chip.key)">
                        <i class="fa-solid fa-xmark"></i>
                    </button>
                </span>
                <button type="button" class="filter-chip-clear" @click="onClearFilters">Clear all</button>
            </div>
        </div>

        <div class="table-section">
            <div class="task-table-card">
                <div class="table-responsive">
                    <table class="table task-table">
                        <thead>
                        <tr>
                            <th>Task Title</th>
                            <th>Full Description</th>
                            <th>Assigned To</th>
                            <th>Report To</th>
                            <th>Created By</th>
                            <th>Organizations Activity</th>
                            <th>Client</th>
                            <th>Project</th>
                            <th>Project Activity</th>
                            <th>Participants</th>
                            <th>Start Date &amp; Time</th>
                            <th>End Date &amp; Time</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th v-if="isWorkStatusTab">Work Status</th>
                            <th>Attachment</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr
                            v-for="(item, i) in tableItems"
                            :key="i"
                            :style="{
                                backgroundColor: getTaskStatusColor(item.status).bg,
                                color: getTaskStatusColor(item.status).text
                            }"
                        >
                            <td class="task-title-cell">
                                <span
                                    class="task-title-link"
                                    :title="item.short_description"
                                    @click="goToEdit(item.id)"
                                >{{ item.short_description }}</span>
                            </td>
                            <td class="task-truncate" :title="item.full_description">{{ item.full_description }}</td>
                            <td class="task-truncate-sm" :title="item.assigned_to">{{ item.assigned_to }}</td>
                            <td class="task-truncate-sm" :title="item.report_to">{{ item.report_to }}</td>
                            <td class="task-truncate-sm" :title="item.user_name">{{ item.user_name }}</td>
                            <td class="task-truncate" :title="item.activity_setup_name">{{ item.activity_setup_name }}</td>
                            <td class="task-truncate-sm" :title="item.client_id">{{ item.client_id }}</td>
                            <td class="task-truncate-sm" :title="item.project_id">{{ item.project_id }}</td>
                            <td class="task-truncate-sm" :title="item.project_activity_id">{{ item.project_activity_id }}</td>
                            <td class="task-truncate-sm" :title="item.participant_id">{{ item.participant_id }}</td>
                            <td class="text-nowrap">{{ item.format_start_time }}</td>
                            <td class="text-nowrap">{{ item.format_end_time }}</td>
                            <td class="task-type-cell">{{ item.type }}</td>
                            <td>
                                <span class="status-pill">{{ item.status }}</span>
                            </td>
                            <td v-if="isWorkStatusTab" class="task-truncate" :title="item.work_status">
                                <span v-if="item.work_status" class="work-status-pill">{{ item.work_status }}</span>
                                <span v-else class="task-no-attachment">&mdash;</span>
                            </td>
                            <td>
                                <button
                                    v-if="item.attachments.length > 0"
                                    class="btn btn-sm attachment-btn"
                                    @click="viewAttachments(item.attachments)"
                                >
                                    <i class="fas fa-eye"></i>
                                    View
                                </button>
                                <span v-else class="task-no-attachment">&mdash;</span>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="!tableItems.length && !loading" class="task-empty">
                    <i class="fa-regular fa-clipboard task-empty-icon"></i>
                    <p class="task-empty-title">No tasks found</p>
                    <p class="task-empty-text">Try adjusting the date range or filters.</p>
                    <button v-if="activeFilterCount" type="button" class="btn btn-sm filter-clear-link" @click="onClearFilters">
                        Clear filters
                    </button>
                </div>
            </div>
        </div>

        <div class="pagination-section">
            <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
        </div>

        <Loader v-if="loading"/>

        <TaskEventScheduler
            v-if="store.state.isModalOpen"
            :startDate="startDate"
            :endDate="endDate"
            :editData="singleActivity"
            :taskStatus="taskStatus"
            :workStatus="workStatus"
            :markAsDoneShow="markAsDoneShow"
            :isCompleted="isCompleted"
            @submitted="onEventSubmitted"
        />

        <ViewAttachment
            ref="viewAttachmentRef"
            :attachments="modalAttachments"
        />
    </div>
</template>

<script setup>
import PageHeader from '@/components/atom/PageHeader.vue';
import { computed, inject, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import Pagination from '@/components/atom/Pagination'
import handleActivity from "@/services/modules/procurement/activity";
import handleChartAccount from '@/services/modules/chartAccount'
import handleProjects from "@/services/modules/procurement/project";
import TaskEventScheduler from '@/components/molecule/company/admin/TaskEventScheduler.vue';
import FilterPopover from '@/components/atom/FilterPopover.vue';
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue';
import handleContact from '@/services/modules/contact';
import handleDropDowns from '@/services/modules/dropdowns';
import { formatDateTime, getTaskStatusColor, resetPaginationPage } from '@/services/utils/global.js';
import ViewAttachment from '@/views/page/company/ngo/voucher/payment/ViewAttachmentModal.vue'

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const taskCalendarFilterList = ref([]);
const projects = ref([]);
const taskCalendarStatusList = ref([]);
const loading = ref(false)
const isUserAccess = ref(false)
const hasWorkStatusAccess = ref(true)
const tableItems = ref([]);
const params = ref({
    offset: 20,
});
const singleActivity = ref(null);
const taskStatus = ref({});
const workStatus = ref({});
const markAsDoneShow = ref(false);
const isCompleted = ref(false);
const startDate = ref('');
const endDate = ref('');
const modalAttachments = ref([]);
const viewAttachmentRef = ref();
const start = ref('');
const end = ref('');
const filterPopoverRef = ref(null);
const organizationActivityDropdown = ref(null);

const getEmptyFilters = () => {
    return {
        contactProfile: '',
        activityGroup: '',
        organizationActivity: '',
        client: '',
        project: '',
        taskCalendarType: '',
        taskCalendarStatus: '',
        subtaskStatus: ''
    };
}

const appliedFilters = ref(getEmptyFilters());
const draftFilters = ref(getEmptyFilters());

const taskTabs = [
    { key: 'today', label: 'Today', status: '', role: 'all', workStatus: '' },
    { key: 'mark_done_pending', label: 'Mark Done Pending', status: 'overdue', role: 'assigned_to', workStatus: '', optionalDate: true },
    { key: 'my_approval_pending', label: 'My Approval Pending', status: 'overdue,completed', role: 'report_to', workStatus: '', optionalDate: true },
    { key: 'others_approval_pending', label: "Other's Approval Pending", status: 'overdue', role: 'participant', workStatus: '', optionalDate: true },
    { key: 'returned', label: 'Return', status: '', role: 'all', workStatus: 'returned', optionalDate: true },
    { key: 'completed', label: 'Completed', status: 'completed', role: 'all', workStatus: '' },
    { key: 'approved', label: 'Approved', status: 'approved', role: 'all', workStatus: '' },
    { key: 'meeting_minute', label: 'Meeting Minute', status: '', role: 'all', workStatus: '' },
    { key: 'rejected', label: 'Reject', status: '', role: 'all', workStatus: 'rejected' },
    { key: 'all_task', label: 'All Task', status: '', role: 'all', workStatus: '' }
];

const activeTab = ref('today');

const visibleTaskTabs = computed(() => {
    if (hasWorkStatusAccess.value) return taskTabs
    return taskTabs.filter(function (tab) {
        return ! tab.workStatus;
    });
});

const isVisibleTab = (tabKey) => {
    return visibleTaskTabs.value.some(function (tab) {
        return tab.key === tabKey;
    });
}

const isKnownTab = (tabKey) => {
    return taskTabs.some(function (tab) {
        return tab.key === tabKey;
    });
}

const isAllTaskTab = computed(() => {
    if (! isKnownTab(activeTab.value)) return true
    return activeTab.value === 'all_task';
})

const isTodayTab = computed(() => {
    if (! isKnownTab(activeTab.value)) return true
    return activeTab.value === defaultTabKey;
})

const isFilterableTab = computed(() => {
    return isAllTaskTab.value || isTodayTab.value;
})

const isWorkStatusTab = computed(() => {
    if (! isKnownTab(activeTab.value)) return false
    return !! getActiveTab().workStatus;
})

const isOptionalDateTab = computed(() => {
    if (! isKnownTab(activeTab.value)) return false
    return !! getActiveTab().optionalDate;
})

const isMeetingMinuteTab = computed(() => {
    return activeTab.value === 'meeting_minute';
})

const subtaskStatusOptions = computed(() => {
    const statuses = Array.isArray(taskCalendarStatusList.value) ? taskCalendarStatusList.value : [];
    return [
        ...statuses.map(function (status) {
            return { value: status.value, name: status.name };
        })
    ];
})

const filterChipMeta = {
    contactProfile: 'Team Member',
    activityGroup: 'Activity Group',
    organizationActivity: 'Organization Activity',
    client: 'Client',
    project: 'Project',
    taskCalendarType: 'Task Role',
    taskCalendarStatus: 'Task Status',
    subtaskStatus: 'Sub-task Status'
};

const { fetchTaskManagementList, fetchSingleTaskActivityLog } = handleActivity()
const { fetchHome } = handleChartAccount()
const { fetchProjects } = handleProjects()
const { fetchContactProfiles } = handleContact()
const { fetchActivitySetupEndpoint } = handleDropDowns()

const getChipText = (key, value) => {
    if (typeof value === 'object') return value.name
    if (key === 'subtaskStatus') {
        const matched = subtaskStatusOptions.value.find(function (option) {
            return option.value === value;
        });
        if (matched) return matched.name;
    }
    return value
}

const tabOwnedKeys = {
    taskCalendarType: () => isFilterableTab.value,
    taskCalendarStatus: () => isFilterableTab.value,
    subtaskStatus: () => isMeetingMinuteTab.value
};

const activeFilterChips = computed(() => {
    return Object.keys(filterChipMeta).filter(key => {
        const owner = tabOwnedKeys[key];
        if (owner && ! owner()) return false
        return !!appliedFilters.value[key];
    }).map(key => {
        return {
            key: key,
            label: filterChipMeta[key],
            text: getChipText(key, appliedFilters.value[key])
        };
    });
})

const activeFilterCount = computed(() => {
    return activeFilterChips.value.length;
})

const defaultTabKey = 'today';

const getActiveTab = () => {
    const found = taskTabs.find(function (tab) {
        return tab.key === activeTab.value;
    });
    if (! found) return taskTabs.find(function (tab) {
        return tab.key === defaultTabKey;
    })
    return found;
}

const getEffectiveDates = () => {
    if (isTodayTab.value) {
        const today = getTodayDate();
        return { start: today, end: today };
    }
    if (isOptionalDateTab.value) {
        const hasRouteDate = !!(route.query.start || route.query.end);
        if (! hasRouteDate) return { start: '', end: '' };
        return { start: route.query.start, end: route.query.end };
    }
    return { start: route.query.start || start.value, end: route.query.end || end.value };
}

const getEffectiveRole = () => {
    if (! isFilterableTab.value) return getActiveTab().role
    if (appliedFilters.value.taskCalendarType) return appliedFilters.value.taskCalendarType
    return getActiveTab().role;
}

const getEffectiveStatus = () => {
    if (! isFilterableTab.value) return getActiveTab().status
    if (appliedFilters.value.taskCalendarStatus) return appliedFilters.value.taskCalendarStatus
    return getActiveTab().status;
}

const getEffectiveWorkStatus = () => {
    return getActiveTab().workStatus;
}

const onClickTab = async (tabKey) => {
    if (activeTab.value === tabKey) return
    activeTab.value = tabKey;
    appliedFilters.value.taskCalendarType = '';
    appliedFilters.value.taskCalendarStatus = '';
    appliedFilters.value.subtaskStatus = '';
    draftFilters.value.taskCalendarType = '';
    draftFilters.value.taskCalendarStatus = '';
    draftFilters.value.subtaskStatus = '';
    const dates = getEffectiveDates();
    start.value = dates.start;
    end.value = dates.end;
    const targetTab = taskTabs.find(function (tab) { return tab.key === tabKey; });
    if (targetTab && targetTab.optionalDate) {
        start.value = '';
        end.value = '';
    }

    let routeQuery = Object.assign({}, route.query);
    routeQuery.tab = tabKey;
    if (start.value) routeQuery.start = start.value; else delete routeQuery.start;
    if (end.value) routeQuery.end = end.value; else delete routeQuery.end;
    await router.push({path: route.path, query: routeQuery});
    resetPaginationPage(store, route, router);
    getTaskManagementList();
}

const onOpenFilter = () => {
    draftFilters.value = Object.assign({}, appliedFilters.value);
}

const onChangeActivityGroup = () => {
    draftFilters.value.organizationActivity = null;
    if (! organizationActivityDropdown.value) return
    organizationActivityDropdown.value.clear();
}

const onApplyFilters = () => {
    appliedFilters.value = Object.assign({}, draftFilters.value);
    resetPaginationPage(store, route, router);
    getTaskManagementList();
}

const onClearFilters = () => {
    appliedFilters.value = getEmptyFilters();
    draftFilters.value = getEmptyFilters();
    if (filterPopoverRef.value) filterPopoverRef.value.close();
    resetPaginationPage(store, route, router);
    getTaskManagementList();
}

const removeFilter = (key) => {
    appliedFilters.value[key] = '';
    draftFilters.value[key] = '';
    resetPaginationPage(store, route, router);
    getTaskManagementList();
}

const getTodayDate = () => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${today.getFullYear()}-${month}-${day}`;
}

const updateDateQuery = async () => {
    let routeQuery = Object.assign({}, route.query);
    if (start.value) routeQuery.start = start.value; else delete routeQuery.start;
    if (end.value) routeQuery.end = end.value; else delete routeQuery.end;
    routeQuery.tab = activeTab.value;
    await router.push({path: route.path, query: routeQuery});
}

const onClickToGo = async () => {
   await updateDateQuery();
   resetPaginationPage(store, route, router);
   getTaskManagementList();
}

const onSubtaskStatusChange = async () => {
    if (! isMeetingMinuteTab.value) return
    resetPaginationPage(store, route, router);
    await getTaskManagementList();
}

const setPaginationData = (data) => {
    store.commit('setPaginationData', data)
};

const setPagination = (data) => {
    setPaginationData({
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    })
}

function navigateToCalendarPage() {
  router.push({name: `task-calendar`, params: route.params, query: route.query});
}

function onClickAddNew() {
  const selectedDate = new Date();
  const now = new Date();

  selectedDate.setHours(now.getHours());
  selectedDate.setMinutes(now.getMinutes());

  const selectedEnd = new Date(selectedDate);
  selectedEnd.setHours(selectedDate.getHours() + 1);

  startDate.value = formatDateTime(selectedDate);
  endDate.value = formatDateTime(selectedEnd);
  singleActivity.value = null;
  taskStatus.value = {};
  workStatus.value = {};
  markAsDoneShow.value = false;
  isCompleted.value = false;
  store.state.isModalOpen = true;
}

const goToEdit = async (activityId) =>  {
  if (!activityId) return;
  markAsDoneShow.value = false;
  isCompleted.value = false;
  loading.value = true;
  singleActivity.value = null;
  taskStatus.value = {};
  workStatus.value = {};

  try {
    const res = await fetchSingleTaskActivityLog(activityId, getQuery());
    if (!res.status) return showError(res.message);
    let response = res.data;
    singleActivity.value = response;
    taskStatus.value = res.task_status;
    if (res.work_status){
      workStatus.value = res.work_status;
    }
    markAsDoneShow.value = true;
    if (['completed', 'approved'].includes(response.status)) {
      isCompleted.value = true;
    }
    store.state.isModalOpen = true;
  } finally {
    loading.value = false;
  }
}

const onEventSubmitted = () => {
   getTaskManagementList();
};

const viewAttachments = (attachments) => {
  modalAttachments.value = [];
  modalAttachments.value = attachments;
  viewAttachmentRef.value && viewAttachmentRef.value.toggleModal();
}

const onPageChange = async (page) => {
    let routeQuery = Object.assign({}, route.query)
    routeQuery.page = page
    await router.push({path: route.path, query: routeQuery})
    await getTaskManagementList();
}

const getTaskManagementList = async () => {
    const query = getQuery();
    loading.value = true
    tableItems.value = []

    await fetchTaskManagementList(query).then(res => {
        hasWorkStatusAccess.value = !! res.has_work_status_access
        if (! isVisibleTab(activeTab.value)) {
            onClickTab(defaultTabKey)
            return
        }
        if (!res.status){
            store.commit("resetPagination")
            return
        }
        tableItems.value = res.data.data;
        isUserAccess.value = res.user_filter_access
        setPagination(res.data)
    }).catch(err => {
        showError(err.message)
    }).finally(() => {
        loading.value = false
    })
}

const exportExcel = () => {
    const token = localStorage.getItem('token') || '';
    const query = getQuery().replace(/&?page=\d+/g, '');
    const url = `${process.env.VUE_APP_BASE_URL}/export/procurement/task-management${query}&_token=${token}&is_export=1`;
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.click();
}

const getQuery = () => {
    const filters = appliedFilters.value;
    let query = `?company_id=${route.params.companyId}&route_name=task-calendar`;
    if (route.query.page) query += '&page=' + route.query.page;
    const dates = getEffectiveDates();
    if (dates.start) query += '&start_date=' + dates.start;
    if (dates.end) query += '&end_date=' + dates.end;
    if (! dates.start && ! dates.end && isOptionalDateTab.value) query += '&skip_date_filter=1';
    const role = getEffectiveRole();
    const status = getEffectiveStatus();
    const tabWorkStatus = getEffectiveWorkStatus();
    if (role) query += `&task_calendar_type=${role}`;
    if (status) query += `&status=${status}`;
    if (tabWorkStatus) query += `&work_status_filter=${tabWorkStatus}`;
    if (filters.project) query += `&project=${filters.project.id}`;
    if (filters.organizationActivity) query += `&organization_activity=${filters.organizationActivity.id}`;
    if (filters.activityGroup) query += `&activity_group=${filters.activityGroup.id}`;
    if (filters.client) query += `&client=${filters.client.id}`;
    if (filters.contactProfile) query += `&contact_profile_id=${filters.contactProfile.id}`;
    if (isMeetingMinuteTab.value) {
        query += `&meeting_minute=1`;
        if (filters.subtaskStatus) query += `&subtask_status=${filters.subtaskStatus}`;
    }
    query += '&offset=' + params.value.offset
    return query;
}

onMounted(async () => {
    if (isKnownTab(route.query.tab)) activeTab.value = route.query.tab;

    const dates = getEffectiveDates();
    start.value = dates.start;
    end.value = dates.end;

    if (start.value !== route.query.start || end.value !== route.query.end) await updateDateQuery();

    let companyQuery = '?company_id=' + route.params.companyId;
    let projectQuery = companyQuery + '&with_donors=1';

    Promise.all([
        getTaskManagementList(),
        fetchHome().then(res => {
          if (!res.data) {
            return;
          }
          if (res.data && res.data.task_calendar_filter){
            taskCalendarFilterList.value = res.data.task_calendar_filter;
          }
          if (res.data && res.data.task_calendar_status){
            taskCalendarStatusList.value = res.data.task_calendar_status;
          }
        }),
        fetchProjects(projectQuery).then(res => {
          if (res.data) {
            projects.value = res.data
          }
        })
    ])
})

</script>

<style scoped>
.task-tabs {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
    padding: 12px 20px 0;
    border-bottom: 1px solid #eaecf0;
}
.task-tab {
    position: relative;
    border: 0;
    background: transparent;
    padding: 9px 14px;
    margin-bottom: -1px;
    border-bottom: 2px solid transparent;
    font-size: 13px;
    font-weight: 500;
    color: #667085;
    white-space: nowrap;
    cursor: pointer;
    transition: color .15s ease, border-color .15s ease;
}
.task-tab:hover {
    color: #101828;
}
.task-tab.is-active {
    color: #7367f0;
    border-bottom-color: #7367f0;
    font-weight: 600;
}
.filter-section {
    padding: 16px 20px 0;
}
.table-section {
    padding: 16px 20px 0;
}
.pagination-section {
    padding: 12px 20px 16px;
}
.filter-bar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
}
.filter-dates {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
}
.filter-bar > :last-child {
    margin-left: auto;
}
.filter-bar-actions {
    display: flex;
    align-items: flex-end;
    gap: 8px;
}
.filter-date-field {
    display: flex;
    flex-direction: column;
}
.filter-label {
    display: block;
    margin-bottom: 4px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #101828;
}
.filter-date-input {
    width: 170px;
    border-radius: 8px;
    border-color: #e4e7ec;
}
.filter-go-btn {
    min-width: 72px;
    border-radius: 8px;
}
.filter-chips {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
}
.filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 8px 4px 10px;
    border-radius: 16px;
    background: #f4f3ff;
    color: #4338ca;
    font-size: 12px;
}
.filter-chip-label {
    color: #7c78a8;
}
.filter-chip-remove {
    border: 0;
    background: transparent;
    color: #7c78a8;
    font-size: 11px;
    line-height: 1;
    padding: 2px;
    cursor: pointer;
}
.filter-chip-remove:hover {
    color: #4338ca;
}
.filter-chip-clear {
    border: 0;
    background: transparent;
    color: #667085;
    font-size: 12px;
    text-decoration: underline;
    cursor: pointer;
}
.task-table-card {
    background: #fff;
    border: 1px solid #eaecf0;
    border-radius: 12px;
    overflow: hidden;
    min-height: 200px;
}
.task-table {
    margin-bottom: 0;
}
.task-table thead th {
    padding: 10px 14px;
    background: #f9fafb;
    border-bottom: 1px solid #eaecf0;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #101828;
    white-space: nowrap;
}
.task-table tbody td {
    height: 48px;
    padding: 8px 14px;
    font-size: 13px;
    line-height: 1.4;
    color: inherit;
    background: transparent;
    vertical-align: middle;
    border-bottom: 1px solid rgba(16, 24, 40, .06);
}
.task-table tbody tr {
    transition: filter .15s ease;
}
.task-table tbody tr:hover {
    filter: brightness(.96);
}
.task-truncate {
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.task-truncate-sm {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.task-type-cell {
    text-transform: capitalize;
}
.task-title-cell {
    max-width: 200px;
    min-width: 140px;
}
.task-title-link {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: inherit;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
}
.task-title-link:hover {
    text-decoration: underline;
}
.status-pill {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 12px;
    background: rgba(255, 255, 255, .65);
    font-size: 11px;
    font-weight: 600;
    text-transform: capitalize;
    white-space: nowrap;
}
.work-status-pill {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 12px;
    background: rgba(255, 255, 255, .75);
    border: 1px solid rgba(16, 24, 40, .12);
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}
.attachment-btn {
    border: 1px solid rgba(16, 24, 40, .15);
    background: rgba(255, 255, 255, .65);
    color: inherit;
    border-radius: 8px;
    font-size: 12px;
}
.attachment-btn:hover {
    background: rgba(255, 255, 255, .9);
    color: inherit;
}
.task-no-attachment {
    opacity: .45;
}
.task-empty {
    padding: 48px 16px;
    text-align: center;
}
.task-empty-icon {
    font-size: 30px;
    color: #d0d5dd;
}
.task-empty-title {
    margin: 12px 0 2px;
    font-size: 15px;
    font-weight: 600;
    color: #344054;
}
.task-empty-text {
    margin-bottom: 12px;
    font-size: 13px;
    color: #98a2b3;
}
.filter-clear-link {
    border: 1px solid #e4e7ec;
    background: #fff;
    color: #475467;
    border-radius: 8px;
}
</style>
